import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone } from "lucide-react";
import { SITE } from "../config/site";
import { api } from "../lib/api";
import { Logo } from "./Logo";

export function Footer() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.categories().then((d) => setCategories((d.items || []).filter((c) => !c.parentId))).catch(() => {});
  }, []);

  return (
    <footer data-testid="site-footer" className="bg-brand-forest text-brand-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo light />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-brand-cream/70">
              Premium, lab-tested cannabis at honest prices. Proudly serving Washington DC and
              select Maryland & Virginia neighborhoods.
            </p>
            <div className="mt-6 flex gap-3">
              <a href={SITE.instagram} data-testid="footer-instagram" aria-label="Instagram" className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-brand-gold hover:text-brand-gold">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={SITE.facebook} data-testid="footer-facebook" aria-label="Facebook" className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-brand-gold hover:text-brand-gold">
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
          <nav aria-label="Shop categories">
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-brand-gold">Shop</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-brand-cream/75">
              <li><Link to="/shop" data-testid="footer-shop-all" className="transition-colors hover:text-brand-gold">Shop All</Link></li>
              {categories.slice(0, 6).map((c) => (
                <li key={c.id}>
                  <Link to={`/category/${c.id}`} data-testid={`footer-category-${c.slug}`} className="transition-colors hover:text-brand-gold">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <nav aria-label="Company">
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-brand-gold">Company</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-brand-cream/75">
              <li><Link to="/about" data-testid="footer-about" className="transition-colors hover:text-brand-gold">About Us</Link></li>
              <li><Link to="/delivery" data-testid="footer-delivery" className="transition-colors hover:text-brand-gold">Delivery Areas</Link></li>
              <li><Link to="/how-to-order" data-testid="footer-how-to-order" className="transition-colors hover:text-brand-gold">How to Order</Link></li>
              <li><Link to="/faq" data-testid="footer-faq" className="transition-colors hover:text-brand-gold">FAQ</Link></li>
              <li><Link to="/contact" data-testid="footer-contact" className="transition-colors hover:text-brand-gold">Contact</Link></li>
            </ul>
          </nav>
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-brand-gold">Get in Touch</h3>
            <ul className="mt-5 space-y-3 text-sm text-brand-cream/75">
              <li>
                <a href={SITE.phoneHref} data-testid="footer-phone" className="flex items-center gap-2.5 transition-colors hover:text-brand-gold">
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="flex items-center gap-2.5 transition-colors hover:text-brand-gold">
                  <Mail className="h-4 w-4" /> {SITE.email}
                </a>
              </li>
              <li className="text-brand-cream/60">{SITE.hours}</li>
              <li className="text-brand-cream/60">Payments: {SITE.payments.join(" · ")}</li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-brand-cream/50 lg:flex-row lg:items-center lg:justify-between">
          <p data-testid="footer-age-notice" className="font-display font-bold uppercase tracking-[0.25em] text-brand-gold/90">
            {SITE.ageLine}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-brand-gold">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-brand-gold">Terms & Conditions</Link>
            <Link to="/age-eligibility" data-testid="footer-age" className="hover:text-brand-gold">Age & Eligibility</Link>
          </div>
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <a
            href="https://webnehtech.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-developer-credit"
            className="group text-brand-cream/50 transition-colors"
          >
            Website developed & SEO Marketing by{" "}
            <span className="rounded-full border border-brand-gold/50 bg-brand-gold/10 px-3 py-1 font-display font-bold text-brand-gold transition-all group-hover:border-brand-gold group-hover:bg-brand-gold group-hover:text-brand-forest">
              webnehtech.com
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}
